/**
 * =====================================================
 * ADMIN VOUCHER MANAGEMENT PAGE
 * =====================================================
 * Features:
 * - Generate voucher batches
 * - View batch list with statistics
 * - View vouchers per batch
 * - Export voucher codes (CSV)
 * - Statistics dashboard
 * - Recent redemptions
 * =====================================================
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Download, Plus, Eye, TrendingUp, Gift, CheckCircle, XCircle, Clock } from 'lucide-react';
import { adminApi } from '../stores/adminStore';

interface VoucherBatch {
  id: string;
  batch_name: string;
  description: string;
  credit_amount: number;
  quantity: number;
  expires_at: string;
  created_at: string;
  total_generated: number;
  total_redeemed: number;
  total_expired: number;
  is_active: boolean;
}

interface Voucher {
  id: string;
  code: string;
  credit_amount: number;
  status: 'active' | 'redeemed' | 'expired' | 'revoked';
  expires_at: string;
  redeemed_by: string | null;
  redeemed_by_name: string | null;  // User name from public.users
  redeemed_at: string | null;
  created_at: string;
}

interface VoucherStats {
  total: number;
  active: number;
  redeemed: number;
  expired: number;
  revoked: number;
  total_credits_distributed: number;
}

interface RedemptionDetail {
  id: string;
  voucher_code: string;
  user_id: string;
  user_name: string;
  user_email: string | null;
  device_token: string;
  redeemed_at: string;
}

interface RedemptionStats {
  total_redemptions: number;
  max_redemptions: number | null;
  redemption_rate: string | number;
  redemptions: RedemptionDetail[];
}

const AdminVoucherManagementPage: React.FC = () => {
  const [batches, setBatches] = useState<VoucherBatch[]>([]);
  const [stats, setStats] = useState<VoucherStats | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<VoucherBatch | null>(null);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [redemptionStats, setRedemptionStats] = useState<RedemptionStats | null>(null);
  const [showRedemptionDetails, setShowRedemptionDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    batch_name: '',
    description: '',
    credit_amount: 500,
    quantity: 10,
    expires_at: '',
    voucher_type: 'TOPUP',  // TOPUP, STD, PRO, BUS, M
    is_mobile_only: false,
    is_shared_code: false,
    max_total_redemptions: null as number | null
  });

  // Load data on mount
  useEffect(() => {
    loadBatches();
    loadStats();
  }, []);

  const loadBatches = async () => {
    try {
      const response = await adminApi.get('/api/voucher/batches');
      setBatches(response.data.data || []);
    } catch (error) {
      console.error('Error loading batches:', error);
      toast.error('Failed to load voucher batches');
    }
  };

  const loadStats = async () => {
    try {
      const response = await adminApi.get('/api/voucher/stats');
      setStats(response.data.data.stats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadBatchVouchers = async (batchId: string) => {
    try {
      setLoading(true);
      const response = await adminApi.get(`/api/voucher/batch/${batchId}`);
      setVouchers(response.data.data.vouchers || []);
      setRedemptionStats(response.data.data.redemption_stats || null);
    } catch (error) {
      console.error('Error loading vouchers:', error);
      toast.error('Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBatch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Debug: Log form data
    console.log('Form Data:', formData);
    
    if (!formData.batch_name || !formData.credit_amount || !formData.quantity || !formData.expires_at) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      setLoading(true);
      
      // Prepare payload
      const payload = {
        batch_name: formData.batch_name,
        description: formData.description || '',
        credit_amount: Number(formData.credit_amount),
        quantity: Number(formData.quantity),
        expires_at: formData.expires_at,
        voucher_type: formData.voucher_type,
        is_mobile_only: formData.is_mobile_only,
        is_shared_code: formData.is_shared_code,
        max_total_redemptions: formData.max_total_redemptions
      };
      
      console.log('Sending payload:', payload);
      
      const response = await adminApi.post('/api/voucher/generate', payload);

      toast.success(`Successfully generated ${response.data.data.total_generated} vouchers!`);
      
      // Reset form and reload data
      setFormData({
        batch_name: '',
        description: '',
        credit_amount: 500,
        quantity: 10,
        expires_at: '',
        voucher_type: 'TOPUP',
        is_mobile_only: false,
        is_shared_code: false,
        max_total_redemptions: null
      });
      setShowGenerateModal(false);
      loadBatches();
      loadStats();
    } catch (error: any) {
      console.error('Error generating batch:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Error response headers:', error.response?.headers);
      console.error('Full error object:', JSON.stringify(error.response?.data, null, 2));
      
      const errorMsg = error.response?.data?.error || error.message || 'Failed to generate vouchers';
      const errorDetails = error.response?.data?.details 
        ? `\n\nDetails: ${JSON.stringify(error.response.data.details)}`
        : '';
      
      toast.error(errorMsg + errorDetails);
    } finally {
      setLoading(false);
    }
  };

  const handleViewBatch = async (batch: VoucherBatch) => {
    setSelectedBatch(batch);
    await loadBatchVouchers(batch.id);
  };

  const handleToggleBatchActive = async (batch: VoucherBatch) => {
    const newStatus = !batch.is_active;
    const action = newStatus ? 'activate' : 'stop';
    
    if (!confirm(`Are you sure you want to ${action} this voucher batch?`)) {
      return;
    }

    try {
      await adminApi.put(`/api/voucher/batch/${batch.id}/toggle-active`, {
        is_active: newStatus
      });
      
      toast.success(`Voucher batch ${newStatus ? 'activated' : 'stopped'} successfully!`);
      await loadBatches();
    } catch (error) {
      console.error('Error toggling batch status:', error);
      toast.error('Failed to update batch status');
    }
  };

  const handleExportVouchers = () => {
    if (!vouchers.length) {
      toast.error('No vouchers to export');
      return;
    }

    // Create CSV content
    const csvContent = [
      'Code,Credit Amount,Status,Expires At,Redeemed At',
      ...vouchers.map(v => 
        `${v.code},${v.credit_amount},${v.status},${v.expires_at},${v.redeemed_at || 'N/A'}`
      )
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vouchers_${selectedBatch?.batch_name.replace(/\s+/g, '_')}_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast.success('Vouchers exported successfully!');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'redeemed': return 'text-blue-600 bg-blue-100';
      case 'expired': return 'text-gray-600 bg-gray-100';
      case 'revoked': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'redeemed': return <CheckCircle className="w-4 h-4" />;
      case 'expired': return <Clock className="w-4 h-4" />;
      case 'revoked': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Voucher Management</h1>
          <p className="text-gray-600 mt-1">Generate and manage voucher codes for Gumroad distribution</p>
        </div>
        <button
          onClick={() => setShowGenerateModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Generate Batch
        </button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Vouchers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString()}</p>
              </div>
              <Gift className="w-10 h-10 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600">{stats.active.toLocaleString()}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Redeemed</p>
                <p className="text-2xl font-bold text-blue-600">{stats.redeemed.toLocaleString()}</p>
              </div>
              <TrendingUp className="w-10 h-10 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Credits Distributed</p>
                <p className="text-2xl font-bold text-purple-600">{stats.total_credits_distributed.toLocaleString()}</p>
              </div>
              <Gift className="w-10 h-10 text-purple-600" />
            </div>
          </div>
        </div>
      )}

      {/* Batch List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Voucher Batches</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credits</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Redeemed</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Redemption Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {batches.map((batch) => {
                const redemptionRate = batch.quantity > 0 
                  ? ((batch.total_redeemed / batch.quantity) * 100).toFixed(1)
                  : '0.0';
                
                return (
                  <tr key={batch.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{batch.batch_name}</div>
                        <div className="text-sm text-gray-500">{batch.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {batch.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircle className="w-3 h-3" />
                          Stopped
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{batch.credit_amount.toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{batch.quantity.toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="text-blue-600 font-medium">{batch.total_redeemed}</span>
                      <span className="text-gray-500"> / {batch.quantity}</span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center">
                        <div className="flex-1">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${redemptionRate}%` }}
                            ></div>
                          </div>
                        </div>
                        <span className="ml-2 text-gray-900">{redemptionRate}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {new Date(batch.expires_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewBatch(batch)}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        <button
                          onClick={() => handleToggleBatchActive(batch)}
                          className={`flex items-center gap-1 ${
                            batch.is_active
                              ? 'text-red-600 hover:text-red-800'
                              : 'text-green-600 hover:text-green-800'
                          }`}
                        >
                          {batch.is_active ? (
                            <>
                              <XCircle className="w-4 h-4" />
                              Stop
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              Activate
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {batches.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No voucher batches yet. Create your first batch to get started!
            </div>
          )}
        </div>
      </div>

      {/* Voucher Detail Modal */}
      {selectedBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{selectedBatch.batch_name}</h3>
                <p className="text-sm text-gray-600 mt-1">{vouchers.length} vouchers</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleExportVouchers}
                  className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
                <button
                  onClick={() => setSelectedBatch(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Close
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <>
                  {/* Shared Voucher Redemption Stats */}
                  {redemptionStats && (
                    <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-600 text-white p-2 rounded-lg">
                            <Gift className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">Shared Voucher Stats</h4>
                            <p className="text-sm text-gray-600">Multiple users can redeem this code</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowRedemptionDetails(true)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
                        >
                          View Details
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg p-4 border border-blue-100">
                          <p className="text-sm text-gray-600 mb-1">Total Redemptions</p>
                          <p className="text-2xl font-bold text-gray-900">{redemptionStats.total_redemptions}</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-blue-100">
                          <p className="text-sm text-gray-600 mb-1">Max Allowed</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {redemptionStats.max_redemptions || '∞'}
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-blue-100">
                          <p className="text-sm text-gray-600 mb-1">Usage Rate</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {redemptionStats.redemption_rate === 'unlimited' 
                              ? '—' 
                              : `${redemptionStats.redemption_rate}%`}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Credits</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Redeemed By</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Redeemed At</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {vouchers.map((voucher) => (
                        <tr key={voucher.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-mono text-gray-900">{voucher.code}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{voucher.credit_amount}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(voucher.status)}`}>
                              {getStatusIcon(voucher.status)}
                              {voucher.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {voucher.redeemed_by ? (
                              <a 
                                href={`/manage/users?userId=${voucher.redeemed_by}`}
                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                title={`User ID: ${voucher.redeemed_by}`}
                              >
                                {voucher.redeemed_by_name || 'Unknown User'}
                            </a>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {voucher.redeemed_at ? new Date(voucher.redeemed_at).toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Redemption Details Modal */}
      {showRedemptionDetails && redemptionStats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Redemption Details</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {redemptionStats.total_redemptions} users redeemed this voucher
                </p>
              </div>
              <button
                onClick={() => setShowRedemptionDetails(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {redemptionStats.redemptions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No redemptions yet
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device Token</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Redeemed At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {redemptionStats.redemptions.map((redemption) => (
                      <tr key={redemption.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">
                          <a 
                            href={`/manage/users?userId=${redemption.user_id}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                            title={`User ID: ${redemption.user_id}`}
                          >
                            {redemption.user_name}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-500 text-xs">
                          {redemption.device_token.substring(0, 20)}...
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {new Date(redemption.redeemed_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-semibold text-gray-900">Generate Voucher Batch</h3>
            </div>
            
            <form onSubmit={handleGenerateBatch} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Batch Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.batch_name}
                  onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Gumroad $10 Package"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Voucher Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ 
                      ...formData, 
                      voucher_type: 'TOPUP', 
                      credit_amount: 500,
                      is_mobile_only: false,
                      is_shared_code: false
                    })}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      formData.voucher_type === 'TOPUP'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold text-sm">💰 Top-Up</div>
                    <div className="text-xs text-gray-600">Credit only (no membership required)</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ 
                      ...formData, 
                      voucher_type: 'STD', 
                      credit_amount: 1000,
                      is_mobile_only: false,
                      is_shared_code: false
                    })}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      formData.voucher_type === 'STD'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold text-sm">🎟️ Standard</div>
                    <div className="text-xs text-gray-600">1,000 credits/month</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ 
                      ...formData, 
                      voucher_type: 'PRO', 
                      credit_amount: 7500,
                      is_mobile_only: false,
                      is_shared_code: false
                    })}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      formData.voucher_type === 'PRO'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold text-sm">⭐ Pro</div>
                    <div className="text-xs text-gray-600">7,500 credits/month</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ 
                      ...formData, 
                      voucher_type: 'BUS', 
                      credit_amount: 25000,
                      is_mobile_only: false,
                      is_shared_code: false
                    })}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      formData.voucher_type === 'BUS'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold text-sm">🏢 Business</div>
                    <div className="text-xs text-gray-600">25,000 credits/month</div>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {formData.voucher_type === 'TOPUP' 
                    ? 'Top-up vouchers add credits directly, can be redeemed by any user'
                    : 'Membership vouchers include 30-day membership activation + credits'
                  }
                </p>
              </div>

              {/* Mobile-Only & Shared Code Options */}
              <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="is_mobile_only"
                    checked={formData.is_mobile_only}
                    onChange={(e) => {
                      const isMobile = e.target.checked;
                      setFormData({
                        ...formData,
                        is_mobile_only: isMobile,
                        voucher_type: isMobile ? 'M' : 'TOPUP',
                        is_shared_code: isMobile,
                        quantity: isMobile ? 1 : 10,
                        credit_amount: isMobile ? 100 : 500
                      });
                    }}
                    className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <label htmlFor="is_mobile_only" className="text-sm font-semibold text-gray-900 cursor-pointer">
                      📱 Mobile App Only (M- prefix)
                    </label>
                    <p className="text-xs text-gray-600 mt-1">
                      This voucher can only be redeemed through MetaPic mobile app. Web redemption will be rejected.
                    </p>
                  </div>
                </div>

                {formData.is_mobile_only && (
                  <>
                    <div className="flex items-start gap-3 pt-2 border-t border-blue-200">
                      <input
                        type="checkbox"
                        id="is_shared_code"
                        checked={formData.is_shared_code}
                        disabled={formData.is_mobile_only}
                        className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                      />
                      <div className="flex-1">
                        <label htmlFor="is_shared_code" className="text-sm font-semibold text-gray-900">
                          🔗 Shared Promo Code
                        </label>
                        <p className="text-xs text-gray-600 mt-1">
                          Single code for everyone (e.g., M-UDEMY-2026). Auto-enabled for mobile-only vouchers.
                        </p>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-blue-200">
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Max Total Redemptions (Optional)
                      </label>
                      <input
                        type="number"
                        value={formData.max_total_redemptions || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          max_total_redemptions: e.target.value ? parseInt(e.target.value) : null
                        })}
                        placeholder="Leave empty for unlimited"
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Limit how many times this promo can be redeemed globally (first-come-first-served)
                      </p>
                    </div>

                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-xs text-yellow-800">
                        <strong>Anti-Abuse Protection:</strong> Each user and device can only redeem this promo once.
                      </p>
                    </div>
                  </>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expires At <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.expires_at ? formData.expires_at.split('T')[0] : ''}
                  onChange={(e) => {
                    if (!e.target.value) {
                      setFormData({ ...formData, expires_at: '' });
                      return;
                    }
                    
                    // Parse date in local timezone and set to end of day (23:59:59)
                    const [year, month, day] = e.target.value.split('-').map(Number);
                    const date = new Date(year, month - 1, day, 23, 59, 59, 999);
                    
                    console.log('Selected date:', e.target.value);
                    console.log('Converted to ISO:', date.toISOString());
                    
                    setFormData({ ...formData, expires_at: date.toISOString() });
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Vouchers will expire at 11:59 PM on this date</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional description"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Credit Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.credit_amount}
                    onChange={(e) => setFormData({ ...formData, credit_amount: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="1"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.voucher_type === 'TOPUP' 
                      ? 'Custom credit amount'
                      : `Customize credits for ${formData.voucher_type} membership (default: ${['STD', 'PRO', 'BUS'].includes(formData.voucher_type) ? formData.credit_amount : 'N/A'})`
                    }
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="1000"
                    disabled={formData.is_shared_code}
                    required
                  />
                  {formData.is_shared_code && (
                    <p className="text-xs text-blue-600 mt-1">Fixed at 1 for shared codes</p>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200 mt-4 pt-4 bg-white sticky bottom-0 -mx-6 px-6 -mb-6 pb-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Generating...' : 'Generate Vouchers'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVoucherManagementPage;
